import { useState, ReactNode, useCallback, useRef } from 'react';
import { Box, Paper, Tabs, Tab, CircularProgress, Typography } from '@mui/material';
import { 
    Add as AddIcon,
    List as ListIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import { PageTitle, PageContainer } from '../../../components/Common';
import { useQueryBuilder, useSimpleQueryBuilder, useSaveQuery, useSaveAndCountQuery, useLinkQueryToGroup, useAvailableGroups } from '../../../hooks/QueryBuilder';
import { useQueryBuilderCache } from '../../../context/QueryBuilderCacheContext';
import { CreateQueryTab, MyQueriesTab, MyListsTab } from '../QueryBuilder/components';
import type { QueryBuilderMode } from '../QueryBuilder/components/CreateQueryTab';
import { toast } from 'react-toastify';
import { convertGroupsToBackendFormat } from '../../../utils/queryBuilderUtils';
import { useLanguage } from '../../../context/LanguageContext';
import type { QueryExecutionResultWithConditions } from '../../../types/QueryBuilder';

interface TabPanelProps {
    children?: ReactNode;
    index: number;
    value: number;
    mounted?: boolean;
}

function TabPanel({ children, value, index, mounted }: TabPanelProps) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {(value === index || mounted) && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function TargettingAndExtractFiles() {
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState(0);
    const [mountedTabs, setMountedTabs] = useState<Set<number>>(new Set([0, 1, 2]));
    const [queryMode, setQueryMode] = useState<QueryBuilderMode>('simple');
    const queryDescriptionRef = useRef('');
    const [descResetKey, setDescResetKey] = useState(0);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [selectedGroupName, setSelectedGroupName] = useState<string>('');
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [queryJSON, setQueryJSON] = useState<QueryExecutionResultWithConditions | null>(null);
    const [queriesRefreshKey, setQueriesRefreshKey] = useState(0);

    const {
        groups: ruleGroups,
        categories,
        isValid,
        addRule,
        removeRule,
        updateRule,
        addGroup,
        removeGroup,
        resetGroups,
        fields,
        loadingFields,
        fieldsError
    } = useQueryBuilder();

    const {
        simpleFields,
        rules: simpleRules,
        groups: simpleGroups,
        isValid: simpleIsValid,
        updateField: updateSimpleField,
        resetFields: resetSimpleFields
    } = useSimpleQueryBuilder();

    const activeGroups = queryMode === 'simple' ? simpleGroups : ruleGroups;
    const activeIsValid = queryMode === 'simple' ? simpleIsValid : isValid;

    const cache = useQueryBuilderCache();
    const { groups: availableGroups, loading: loadingGroups, refetch: refetchGroups } = useAvailableGroups();
    const { saveQuery: saveQueryApi } = useSaveQuery();
    const { saveAndCountQuery, loading: executingAndCounting } = useSaveAndCountQuery();
    const { linkQueryToGroup } = useLinkQueryToGroup();

    const handleCreateGroup = async (groupName: string) => {
        setNewGroupName(groupName);
        setSelectedGroupId(-1);
        setSelectedGroupName(groupName);
    };

    const handleGroupChange = (groupId: number | null, groupName?: string) => {
        setSelectedGroupId(groupId);
        setSelectedGroupName(groupName || '');
        setNewGroupName('');
    };

    const resetQueryForm = useCallback(() => {
        resetGroups();
        resetSimpleFields();
        queryDescriptionRef.current = '';
        setDescResetKey(k => k + 1);
        setSelectedGroupId(null);
        setSelectedGroupName('');
        setNewGroupName('');
        setQueryJSON(null);
    }, [resetGroups, resetSimpleFields]);

    const handleExecuteQuery = async () => {
        const description = queryDescriptionRef.current.trim();
        if (!activeIsValid) return toast.warning('Veuillez compléter toutes les règles');
        if (!description) return toast.warning('Veuillez renseigner un nom de requête avant d\'exécuter');

        const groupNameToUse = selectedGroupName || newGroupName;
        const toastId = toast.loading(
            groupNameToUse.trim()
                ? 'Exécution et création du groupe en cours...'
                : 'Exécution de la requête en cours...'
        );

        try {
            const result = await saveAndCountQuery({
                description,
                groups: activeGroups,
                groupId: selectedGroupId && selectedGroupId > 0 ? selectedGroupId : undefined,
                source: queryMode === 'simple' ? 'SIMPLE' : 'ADVANCED'
            });

            if (!result) {
                toast.update(toastId, {
                    render: "Erreur lors de l'exécution de la requête",
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000
                });
                return;
            }

            let successMessage = 'Requête sauvegardée, comptage lancé';
            if (result.mode === 'sync') {
                setQueryJSON({
                    query_id: result.queryId.toString(),
                    description,
                    query_type: 1,
                    count: result.count,
                    conditions: [convertGroupsToBackendFormat(activeGroups)],
                });
                successMessage = `Requête exécutée : ${result.count} résultat(s) trouvé(s)`;
            }

            if (groupNameToUse.trim()) {
                if (!result.queryId) {
                    toast.update(toastId, {
                        render: 'Requête créée, mais impossible de la lier au groupe',
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000
                    });
                    return;
                }

                toast.update(toastId, { render: 'Création du groupe en cours...', isLoading: true });

                const linked = await linkQueryToGroup({
                    queryId: result.queryId,
                    groupQueryName: groupNameToUse,
                    queryName: description
                });
                if (!linked) {
                    toast.update(toastId, {
                        render: 'Erreur lors de la création du groupe',
                        type: 'error',
                        isLoading: false,
                        autoClose: 5000
                    });
                    return;
                }
                await refetchGroups();
            }

            resetQueryForm();
            cache?.invalidate('saved_queries|');
            setQueriesRefreshKey(k => k + 1);
            toast.update(toastId, {
                render: successMessage,
                type: 'success',
                isLoading: false,
                autoClose: 5000
            });
        } catch {
            toast.update(toastId, {
                render: "Erreur lors de l'exécution de la requête",
                type: 'error',
                isLoading: false,
                autoClose: 5000
            });
        }
    };

    const handleSaveQuery = async () => {
        const description = queryDescriptionRef.current.trim();
        if (!activeIsValid || !description) return toast.warning('Nom requis');

        const groupNameToUse = selectedGroupName || newGroupName;
        const toastId = toast.loading(
            groupNameToUse.trim()
                ? 'Sauvegarde et création du groupe en cours...'
                : 'Sauvegarde de la requête en cours...'
        );

        setSaving(true);
        try {
            const result = await saveQueryApi({
                description,
                groups: activeGroups,
                groupId: selectedGroupId && selectedGroupId > 0 ? selectedGroupId : undefined,
                source: queryMode === 'simple' ? 'SIMPLE' : 'ADVANCED'
            });
            
            if (result && result.queryId) {
                if (groupNameToUse.trim()) {
                    toast.update(toastId, { render: 'Création du groupe en cours...', isLoading: true });

                    const linked = await linkQueryToGroup({
                        queryId: result.queryId,
                        groupQueryName: groupNameToUse,
                        queryName: description
                    });
                    if (!linked) {
                        toast.update(toastId, {
                            render: 'Erreur lors de la création du groupe',
                            type: 'error',
                            isLoading: false,
                            autoClose: 5000
                        });
                        return;
                    }
                    await refetchGroups();
                }
                
                resetQueryForm();
                cache?.invalidate('saved_queries|');
                setQueriesRefreshKey(k => k + 1);
                toast.update(toastId, {
                    render: 'Requête sauvegardée avec succès',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000
                });
            } else {
                toast.update(toastId, {
                    render: 'Erreur lors de la sauvegarde de la requête',
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000
                });
            }
        } catch {
            toast.update(toastId, {
                render: 'Erreur lors de la sauvegarde de la requête',
                type: 'error',
                isLoading: false,
                autoClose: 5000
            });
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => resetQueryForm();

    if (loadingFields) {
        return (
            <PageContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress size={60} />
                </Box>
            </PageContainer>
        );
    }

    if (fieldsError) {
        return (
            <PageContainer>
                <Paper sx={{ p: 3, bgcolor: '#fff3cd', border: '1px solid #ffc107' }}>
                    <Typography color="error">Erreur: {fieldsError}</Typography>
                </Paper>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageTitle>{t('menu.targetingExtractFiles')}</PageTitle>

            <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue: number) => {
                        setActiveTab(newValue);
                        setMountedTabs(prev => new Set([...prev, newValue]));
                    }}
                    sx={{ 
                        borderBottom: '1px solid #e0e0e0',
                        '& .MuiTab-root': { 
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            minHeight: 64,
                            py: 2
                        }
                    }}
                >
                    <Tab icon={<AddIcon />} iconPosition="start" label="Nouvelle requête" />
                    <Tab icon={<ListIcon />} iconPosition="start" label="Mes requêtes" />
                    <Tab icon={<UploadIcon />} iconPosition="start" label="Mes listes" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    <TabPanel value={activeTab} index={0} mounted={mountedTabs.has(0)}>
                        <CreateQueryTab
                            mode={queryMode}
                            onModeChange={setQueryMode}
                            groups={ruleGroups}
                            categories={categories}
                            fields={fields}
                            simpleFields={simpleFields}
                            simpleRules={simpleRules}
                            onUpdateSimpleField={updateSimpleField}
                            queryJSON={queryJSON}
                            isValid={activeIsValid}
                            executingQuery={executingAndCounting}
                            saving={saving}
                            selectedGroupId={selectedGroupId}
                            newGroupName={newGroupName}
                            availableGroups={availableGroups}
                            loadingGroups={loadingGroups}
                            descResetKey={descResetKey}
                            onRefetchGroups={refetchGroups}
                            onQueryDescriptionChange={(v) => { queryDescriptionRef.current = v; }}
                            onGroupIdChange={handleGroupChange}
                            onCreateGroup={handleCreateGroup}
                            onAddRule={addRule}
                            onUpdateRule={updateRule}
                            onRemoveRule={removeRule}
                            onAddGroup={addGroup}
                            onRemoveGroup={removeGroup}
                            onReset={handleReset}
                            onExecute={handleExecuteQuery}
                            onSave={handleSaveQuery}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1} mounted={mountedTabs.has(1)}>
                        <MyQueriesTab availableGroups={availableGroups} loadingGroups={loadingGroups} onRefetchGroups={refetchGroups} refreshKey={queriesRefreshKey} />
                    </TabPanel>

                    <TabPanel value={activeTab} index={2} mounted={mountedTabs.has(2)}>
                        <MyListsTab />
                    </TabPanel>
                </Box>
            </Paper>
        </PageContainer>
    );
}

export default TargettingAndExtractFiles;

